import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { JsonResponse } from '../shared/api/backend-config';
import { AuthDataJSON } from './sign-in/sign-in.component';
import { AuthDataRegisterJSON } from './sign-up/register-individual/register-individual.component';
import { GoogleAuth0Response } from './oauth';
import { SubscriptionPlans } from './user/user.model';
import { Router } from '@angular/router';
import { REDIRECT_AFTER_LOGIN_PATH } from '../app.component';

import { ACCESS_TOKEN, REFRESH_TOKEN } from './auth-tokens';
import { SessionExpiredError } from './session-expired.error';
import { SignUpTrackingService } from './sign-up-tracking.service';
import { SalesForecastFunnelService } from '../sales-forecast/sales-forecast-funnel.service';

// Re-exported for backward compatibility with existing imports
export { ACCESS_TOKEN, REFRESH_TOKEN } from './auth-tokens';
export const INDIVIDUAL = 'INDIVIDUAL';
export const ENTERPRISE = 'ENTERPRISE';

export interface RegisterData {
  email: string;
  firstName: string;
  lastName: string | null;
  password: string;
  category: typeof INDIVIDUAL | typeof ENTERPRISE;
  companyName?: string;
}

export interface SubscriptionRegion {
  regionType: 'COUNTY' | 'STATE' | 'COUNTRY';
  id: string;
  displayName: string;
}

export interface UserSubscription {
  plan: SubscriptionPlans;
  // unix timestamp [UTC] when subscription ends. Null for lifetime
  untilUtc: number | null;
  // for subscriptions which are actually one-time packages
  isPackage: boolean;
  isLifetime: boolean;
  isAutoRenewable: boolean;
  regions: SubscriptionRegion[];
}
export interface UserPersonalData extends UserInfo {
  companyName?: string;
  companyPosition?: string;
  category: typeof INDIVIDUAL | typeof ENTERPRISE;
  createdAt: string;
  emailConfirmed: boolean;
  isAdmin: boolean;
  subscription: UserSubscription | null;
  phone?: string;
  zipCode?: string;
  city?: string;
  stateCounty?: string;
  country?: string;
  realtorsAssociationMemberId?: string;
}

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
}

export interface TokensStorage {
  [ACCESS_TOKEN]: string | null;
  [REFRESH_TOKEN]: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  /**
   * Actual data about logged user.
   */
  public userPersonalData: BehaviorSubject<UserPersonalData | null> =
    new BehaviorSubject<UserPersonalData | null>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
    private signUpTracking: SignUpTrackingService,
    private funnel: SalesForecastFunnelService
  ) {
    // @ts-ignore
    window.loginByAngularApi = (email, pass) => {
      //@ts-ignore
      zone.run(() => {
        return this.login(email, pass);
      });
    };
  }

  /**
   * The page view an auth request belongs to, or nothing outside the
   * sales-forecast pages. Forwarded so the server's REGISTERED row carries
   * the same `pageViewId` as the browser's PAGE_OPENED: without it a sign-up
   * can only be attributed to a page view by guessing from timestamps.
   *
   * Extra query params are ignored by a backend that does not read them yet,
   * so this is safe to ship ahead of that.
   */
  private funnelOriginParams(): Record<string, string> {
    const placement = this.signUpTracking.currentPlacement();
    return {
      ...(this.funnel.currentPageView() ?? {}),
      ...(placement ? { placement } : {}),
    };
  }

  public login(
    email: string,
    password: string,
    redirect = true
  ): Observable<JsonResponse<AuthDataJSON>> {
    const body = { email, password };
    return this.http
      .post<JsonResponse<AuthDataJSON>>(`${environment.apiUrl}user/login`, body)
      .pipe(
        tap((res) => {
          this.setTokens(res.object.tokens.access, res.object.tokens.refresh);
          this.getUserInfo().subscribe(() => {
            if (redirect) {
              this.redirectAfterAuth();
            }
          });
        }),
        catchError((err) => {
          console.error('in auth service login: ', err);
          throw err;
        })
      );
  }

  public loginGoogle(
    token: GoogleAuth0Response,
    redirect = true
  ): Observable<JsonResponse<AuthDataJSON>> {
    return this.http
      .post<JsonResponse<AuthDataJSON>>(
        `${environment.apiUrl}user/login/google`,
        token,
        {
          params: this.funnelOriginParams(),
        }
      )
      .pipe(
        tap((res) => {
          this.setTokens(res.object.tokens.access, res.object.tokens.refresh);
          // One endpoint serves both cases, so only the backend can tell a
          // new account from a returning one. Until it says so, this counts
          // as a login: undercounting sign-ups is recoverable, reporting
          // returning users as conversions is not.
          if (res.object.registered) {
            this.signUpTracking.signedUp('google');
          } else {
            this.signUpTracking.signedIn('google');
          }
          this.refreshUserInfo();
          if (redirect) {
            this.redirectAfterAuth();
          }
        }),
        catchError((err) => {
          console.error('in auth service login: ', err);
          throw err;
        })
      );
  }

  public register(
    data: RegisterData
  ): Observable<JsonResponse<AuthDataRegisterJSON>> {
    return this.http
      .post<JsonResponse<AuthDataRegisterJSON>>(
        `${environment.apiUrl}user/register`,
        data,
        {
          params: this.funnelOriginParams(),
        }
      )
      .pipe(
        tap((res) => {
          this.setTokens(res.object.access, res.object.refresh);
          // Reaching here means the account exists: the endpoint answers with
          // tokens only on success, and every failure path throws instead.
          this.signUpTracking.signedUp('email');
          this.refreshUserInfo();
        }),
        catchError((err) => {
          console.error('in auth service register: ', err);
          throw err;
        })
      );
  }

  /**
   * Fails without asking the backend when there is nothing to refresh with.
   *
   * `getTokens()` reads straight from localStorage, so a missing key is `null`
   * and the request body was `{"refreshToken":null}` - 21 bytes, which is how
   * this was found in a Sentry trace. The backend can only answer 400 to that,
   * and it did, 8.8K times for 555 visitors holding an access token whose
   * refresh token was gone. A visitor in that state is signed out; the only
   * thing the round trip added was noise, six retries of it.
   */
  public refreshToken(): Observable<JsonResponse<AuthDataJSON>> {
    const refreshToken = this.getTokens()[REFRESH_TOKEN];
    if (!refreshToken) {
      return throwError(
        () => new SessionExpiredError('no refresh token is stored')
      );
    }
    return this.http
      .post<
        JsonResponse<AuthDataJSON>
      >(`${environment.apiUrl}user/token/refresh`, { refreshToken: refreshToken })
      .pipe(
        tap((res) => {
          this.setTokens(res.object.tokens.access, res.object.tokens.refresh);
        })
      );
  }

  public redirectAfterAuth(): void {
    if (this.getBacklinkFromUrl()) {
      console.log('Navigating to backlink:', this.getBacklinkFromUrl());
      this.router.navigateByUrl(this.getBacklinkFromUrl()!);
      return;
    }

    const savedPath = localStorage.getItem(REDIRECT_AFTER_LOGIN_PATH);
    if (savedPath) {
      console.log('Navigating to saved path:', savedPath);
      this.router.navigateByUrl(savedPath);
    } else {
      this.router.navigate(['/']);
    }
    localStorage.removeItem(REDIRECT_AFTER_LOGIN_PATH);
  }

  /**
   * Request user info from server.
   * Subscribe to userPersonalData instead if possible.
   */
  // FIXME: this subscribes to a cold HttpClient observable and hands the same
  // one back, so every caller that subscribes fires a second GET /user/ (see
  // the login flow above). The internal subscription is also never torn down -
  // harmless only because the request completes on its own. The fix is
  // `.pipe(tap(...), shareReplay(1))` with the callers doing the subscribing,
  // but every caller of this shared service has to be checked first.
  public getUserInfo(): Observable<JsonResponse<UserPersonalData>> {
    const observable = this.http.get<JsonResponse<UserPersonalData>>(
      `${environment.apiUrl}user/`
    );
    observable.subscribe((data) => {
      this.userPersonalData.next(data.object);
    });
    return observable;
  }

  public requestPasswordRecovery(email: string): Observable<any> {
    return this.http.post<JsonResponse<any>>(
      `${environment.apiUrl}password/reset?email=${email.toLowerCase()}`,
      {}
    );
  }

  public resetPassword(newPassword: string, token: string): Observable<any> {
    return this.http.put<JsonResponse<any>>(
      `${environment.apiUrl}password?token=${token}`,
      { newPassword: newPassword }
    );
  }

  /**
   * Re-request user info from server & trigger userPersonalData update.
   */
  public refreshUserInfo(): void {
    this.getUserInfo();
  }

  public updateUserInfo(
    data: Partial<UserPersonalData>
  ): Observable<JsonResponse<UserPersonalData>> {
    return this.http.post<JsonResponse<UserPersonalData>>(
      `${environment.apiUrl}user/`,
      data
    );
  }

  public logout(): void {
    localStorage.removeItem(REDIRECT_AFTER_LOGIN_PATH);
    this.userPersonalData.next(null);

    this.router.navigate(['/sign-in']);
    this.clearTokens();
  }

  public clearTokens(): void {
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
  }

  public getTokens(): TokensStorage {
    return {
      [ACCESS_TOKEN]: localStorage.getItem(ACCESS_TOKEN),
      [REFRESH_TOKEN]: localStorage.getItem(REFRESH_TOKEN),
    };
  }

  public setTokens(access: string, refresh: string): void {
    localStorage.setItem(ACCESS_TOKEN, access);
    localStorage.setItem(REFRESH_TOKEN, refresh);
  }

  public hasAccessToken(): boolean {
    const token = this.getTokens()[ACCESS_TOKEN];
    return !!token;
  }

  public getUserEmail(): string {
    return this.userPersonalData.getValue()?.email!;
  }

  private getBacklinkFromUrl(): string | null {
    return new URLSearchParams(window.location.search).get('backlink');
  }
}
